import { useCallback, useEffect, useRef, useState } from 'react';
import { createGlobalState } from 'react-use';
import { useActiveProject } from '../../../../hooks';
import { useMixpanel } from '../../../../hooks/useMixpanel';
import { useApiClient } from '../../../../hooks/useRestAPIHooks';
import {
  bucketClientObservedDuration,
  toClientObservedTerminalOutcome,
} from '../../../../util/clientObservedGeneration';

type CompositeReportInvocationStatus =
  | 'scheduled'
  | 'pending'
  | 'waiting'
  | 'processing'
  | 'failed'
  | 'completed'
  | 'expired';

type CompositeReportTask = {
  id: string;
  status: CompositeReportInvocationStatus;
  created: number;
};

const isTaskResolved = (task: { status: CompositeReportInvocationStatus }) =>
  ['failed', 'completed', 'expired'].includes(task.status);

// Maximum consecutive network/server errors before giving up on a poll loop.
// Transient blips (timeouts, 5xx) should not terminate tracking.
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

// Global state keyed by subjectId so sibling panels share the same task per
// patient without leaking one subject's state into another's view.
// Exported so tests can reset the global state between test cases.
export const useTasksBySubject = createGlobalState<
  Record<string, CompositeReportTask | undefined>
>({});

export type InvokeResult = 'invoked' | 'conflict';

export type SectionsToGenerate =
  | { type: 'all' }
  | { type: 'subset'; subset: { template_id: string }[] };

export type SectionInstruction = {
  template_id: string;
  instructions: string;
};

/** Options accepted by the `invoke` function returned by `useCompositeReportGeneration`. */
export type InvokeOptions = {
  sectionsToGenerate?: SectionsToGenerate;
  instructions?: SectionInstruction[];
};

export const useCompositeReportGeneration = ({
  subjectId,
  onGenerationSuccess,
  onGenerationFailure,
}: {
  subjectId: string;
  onGenerationSuccess?: () => void;
  onGenerationFailure?: () => void;
}) => {
  const [project] = useActiveProject();
  const apiClient = useApiClient();
  const { track } = useMixpanel();

  const [tasksBySubject, setTasksBySubject] = useTasksBySubject();
  const task = (tasksBySubject ?? {})[subjectId];

  // Keep a ref that always reflects the latest global map so setTask never
  // spreads a stale snapshot — prevents a poll update from dropping another
  // subject's entry when it merges into the map.
  const tasksBySubjectRef = useRef<
    Record<string, CompositeReportTask | undefined>
  >(tasksBySubject ?? {});
  useEffect(() => {
    tasksBySubjectRef.current = tasksBySubject ?? {};
  }, [tasksBySubject]);

  const setTask = useCallback(
    (t: CompositeReportTask | undefined) => {
      setTasksBySubject({ ...tasksBySubjectRef.current, [subjectId]: t });
    },
    [setTasksBySubject, subjectId],
  );

  const [isInvoking, setIsInvoking] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Clear stale error when subject changes so a previous patient's poll
  // failure doesn't bleed into the new subject's view.
  useEffect(() => {
    setError(undefined);
  }, [subjectId]);

  // Tracks the task ID for which this instance has already fired a terminal
  // callback. Cleared whenever a new task?.id is observed so a re-attached task
  // (after an error + remount / 409-reattach) can fire callbacks again.
  const callbackFiredForTaskRef = useRef<string | undefined>(undefined);

  // Locally accepted invoke only — never attribute mount-hydrated/conflict tasks.
  const localAcceptedTaskRef = useRef<{
    taskId: string;
    startedAt: number;
    scope: 'full' | 'section_subset';
  } | null>(null);
  const emittedCompletionTaskIdRef = useRef<string | null>(null);

  // On mount (or when subjectId changes), check once for an already-running task.
  useEffect(() => {
    let cancelled = false;
    const checkForActiveTask = async () => {
      try {
        const resp = await apiClient.request(
          'GET /v1/agents-v2/composite-report/invocations',
          { subject_id: subjectId },
        );
        if (cancelled) return;
        const invocations = resp.data as CompositeReportTask[];
        const active = invocations?.find(inv => !isTaskResolved(inv));
        // Only set when an active task is found. Do NOT clear on empty response:
        // a concurrent invoke() may have just created a task that hasn't appeared
        // in the invocations list yet. Stale tasks are cleaned up by the polling
        // effect when GET :task_id returns resolved/missing status.
        if (active) {
          setTask(active);
        }
      } catch {
        // Non-fatal: if check fails, do not block the UI
      }
    };
    checkForActiveTask();
    // Re-run if subjectId changes so we don't keep a different patient's task.
    return () => {
      cancelled = true;
    };
  }, [subjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the active task until it resolves.
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined = undefined;
    let cancelled = false;
    let isPolling = false;
    let consecutiveErrors = 0;

    // Clear the callback guard whenever we start observing a new task ID so
    // that re-attached tasks (error → remount / 409-reattach) can still fire
    // onGenerationSuccess even if onGenerationFailure fired in a prior session.
    callbackFiredForTaskRef.current = undefined;

    const stopInterval = () => {
      cancelled = true;
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };

    if (!task?.id) {
      return stopInterval;
    }

    const fireSuccess = () => {
      if (callbackFiredForTaskRef.current === task.id) return;
      callbackFiredForTaskRef.current = task.id;
      try {
        onGenerationSuccess?.();
      } catch {
        // Prevent a throwing callback from breaking cleanup
      }
    };

    const fireFailure = () => {
      if (callbackFiredForTaskRef.current === task.id) return;
      callbackFiredForTaskRef.current = task.id;
      try {
        onGenerationFailure?.();
      } catch {
        // Prevent a throwing callback from breaking cleanup
      }
    };

    const checkForUpdates = async () => {
      if (isPolling) {
        return;
      }
      isPolling = true;
      try {
        const response = await apiClient.request(
          'GET /v1/agents-v2/composite-report/invocations/:task_id',
          { task_id: task.id },
        );
        // Discard response if the effect has been cleaned up (subject/task
        // changed or component unmounted) to prevent stale callbacks.
        if (cancelled) return;

        consecutiveErrors = 0; // successful response resets the error counter
        const invocation = response.data as CompositeReportTask | undefined;

        if (!invocation || isTaskResolved(invocation)) {
          stopInterval();
          setTask(undefined);
        } else {
          setTask(invocation);
        }

        // Fire callbacks only on known terminal statuses, never on falsy response.
        if (invocation?.status === 'completed') {
          fireSuccess();
        } else if (
          invocation?.status === 'failed' ||
          invocation?.status === 'expired' ||
          !invocation
        ) {
          fireFailure();
        }

        const terminalOutcome = invocation
          ? toClientObservedTerminalOutcome(invocation.status)
          : null;
        if (
          terminalOutcome &&
          localAcceptedTaskRef.current?.taskId === task.id &&
          emittedCompletionTaskIdRef.current !== task.id
        ) {
          emittedCompletionTaskIdRef.current = task.id;
          track('composite_report_generation_completed', {
            outcome: terminalOutcome,
            durationBucket: bucketClientObservedDuration(
              localAcceptedTaskRef.current.startedAt,
            ),
            scope: localAcceptedTaskRef.current.scope,
          });
        }
      } catch (err) {
        if (cancelled) return;
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
          // Only give up after repeated failures — transient errors should not
          // terminate tracking while the server-side job may still be running.
          stopInterval();
          setTask(undefined);
          setError(err instanceof Error ? err : new Error(String(err)));
          fireFailure();
        }
        // Otherwise swallow the error and let the next interval tick retry.
      } finally {
        isPolling = false;
      }
    };

    intervalId = setInterval(checkForUpdates, 5000);
    checkForUpdates();

    return stopInterval;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, task?.id, track]);

  const invoke = useCallback(
    async (
      crdId: string,
      opts?: {
        sectionsToGenerate?: SectionsToGenerate;
        instructions?: SectionInstruction[];
      },
    ): Promise<InvokeResult> => {
      setIsInvoking(true);
      setError(undefined);
      // Scope from bounded selection only — never section titles or IDs.
      const scope =
        opts?.sectionsToGenerate?.type === 'subset' ? 'section_subset' : 'full';
      const startedAt = Date.now();
      try {
        const body: Record<string, unknown> = {
          subject_id: subjectId,
          project_id: project.id,
          crd_id: crdId,
        };
        if (opts?.sectionsToGenerate) {
          body.sections_to_generate = opts.sectionsToGenerate;
        }
        if (opts?.instructions?.length) {
          body.instructions = opts.instructions;
        }
        const res = await apiClient.request(
          'POST /v1/agents-v2/composite-report/invoke',
          body as Parameters<typeof apiClient.request>[1],
        );
        const taskId =
          (res.data as { task_id?: string })?.task_id ??
          res.headers?.location?.split('/').pop();
        if (!taskId) {
          // 201 with no parseable task id is a server contract violation.
          // Surface as an error rather than silently returning 'invoked' with
          // polling never starting.
          throw new Error('Composite report invoked but no task ID returned');
        }
        localAcceptedTaskRef.current = { taskId, startedAt, scope };
        emittedCompletionTaskIdRef.current = null;
        setTask({ id: taskId, status: 'pending', created: Date.now() });
        track('composite_report_generation_requested', {
          outcome: 'accepted',
          scope,
        });
        return 'invoked';
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 409) {
          // Load the existing in-progress task so polling starts immediately
          // without waiting for a remount. Do not attribute terminals to this
          // conflict — the job was not newly accepted by this mount.
          localAcceptedTaskRef.current = null;
          track('composite_report_generation_requested', {
            outcome: 'error',
            scope,
          });
          try {
            const conflictResp = await apiClient.request(
              'GET /v1/agents-v2/composite-report/invocations',
              { subject_id: subjectId },
            );
            const invocations = conflictResp.data as CompositeReportTask[];
            const active = invocations?.find(inv => !isTaskResolved(inv));
            if (active) {
              setTask(active);
            }
          } catch {
            // Non-fatal: return conflict regardless
          }
          return 'conflict';
        }
        track('composite_report_generation_requested', {
          outcome: 'error',
          scope,
        });
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsInvoking(false);
      }
    },
    [apiClient, subjectId, project.id, setTask, track],
  );

  const taskStatus = task?.status;
  const isPolling =
    taskStatus === 'pending' ||
    taskStatus === 'processing' ||
    taskStatus === 'waiting' ||
    taskStatus === 'scheduled';

  return {
    invoke,
    isInvoking,
    isPolling,
    taskStatus,
    error,
  };
};
